import { ApolloClient, ApolloLink, HttpLink, InMemoryCache } from '@apollo/client';
import { createFragmentRegistry } from '@apollo/client/cache';
import { SetContextLink } from '@apollo/client/link/context';
import { ErrorLink } from '@apollo/client/link/error';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { ApolloProvider } from '@apollo/client/react';
import { displayApolloError } from '@optools/components/notifications/apollo';
import { MlbGatewayProxy } from '@optools/services/config/types';
import {
  BTC_BROADCAST_FRAGMENT,
  BTC_CONTENT_ASSETS_FRAGMENT,
  BTC_CONTENT_FRAGMENT,
  BTC_GAME_FRAGMENT,
  BTC_PIPELINE_FRAGMENT,
  BTC_PIPELINE_MEDIA_UPDATE_FRAGMENT,
  COPYRIGHT_STREAM_FRAGMENT,
  MULTICAST_MAPPING_FRAGMENT,
} from '@optools/services/operator-gateway/btc/fragments';
import {
  ESNI_MEDIA_PROCESSING_RESULT_FIELDS,
  ESNI_RULE_FRAGMENT,
  MEDIA_POLICY_FRAGMENT,
  VIEWING_POLICY_FRAGMENT,
} from '@optools/services/operator-gateway/esni/fragments';
import { TEAM_FRAGMENT } from '@optools/services/operator-gateway/fragments';
import { PIPELINE_CONFIG_FRAGMENT } from '@optools/services/operator-gateway/pipeline-config/fragments';
import { OperationTypeNode } from 'graphql';
import { createClient } from 'graphql-ws';
import { ReactNode, useMemo } from 'react';

function createApolloClientConfig(uri: string, wsUrl: string) {
  const authLink = new SetContextLink((prevContext) => {
    const oktaStorage = localStorage.getItem('okta-token-storage');
    const token = oktaStorage ? JSON.parse(oktaStorage)?.accessToken.accessToken : '';
    return {
      headers: {
        ...prevContext.headers,
        authorization: token ? `Bearer ${token}` : '',
      },
    };
  });

  const errorLink = new ErrorLink(({ error, operation }) => {
    const { skipErrorNotification } = operation.getContext();
    if (!skipErrorNotification) {
      displayApolloError({ error, title: operation.operationName ?? 'Server Error' });
    }
  });

  const httpLink = new HttpLink({ uri });
  const wsLink = new GraphQLWsLink(createClient({ url: wsUrl }));
  const queryLink = ApolloLink.split(
    ({ operationType }) => operationType === OperationTypeNode.SUBSCRIPTION,
    wsLink,
    httpLink,
  );

  const link = ApolloLink.from([authLink, errorLink, queryLink]);

  const cache = new InMemoryCache({
    fragments: createFragmentRegistry(
      BTC_BROADCAST_FRAGMENT,
      BTC_CONTENT_FRAGMENT,
      BTC_CONTENT_ASSETS_FRAGMENT,
      BTC_PIPELINE_FRAGMENT,
      BTC_PIPELINE_MEDIA_UPDATE_FRAGMENT,
      BTC_GAME_FRAGMENT,
      MEDIA_POLICY_FRAGMENT,
      PIPELINE_CONFIG_FRAGMENT,
      VIEWING_POLICY_FRAGMENT,
      MULTICAST_MAPPING_FRAGMENT,
      COPYRIGHT_STREAM_FRAGMENT,
      TEAM_FRAGMENT,
      ESNI_RULE_FRAGMENT,
      ESNI_MEDIA_PROCESSING_RESULT_FIELDS,
    ),
    typePolicies: {
      Broadcast: {
        keyFields: ['mediaId'],
      },
      Content: {
        keyFields: ['mediaId'],
      },
      Game: {
        fields: {
          pipelines: {
            merge(_existing = [], incoming: unknown[]) {
              return incoming;
            },
          },
        },
        keyFields: ['gamePk'],
      },
      LocationAsset: {
        keyFields: ['assetId', 'cdn'],
      },
      MediaPolicy: {
        fields: {
          viewingPolicies: {
            merge(_existing = [], incoming: unknown[]) {
              return incoming;
            },
          },
        },
        keyFields: ['id'],
      },
      PipelineConfigInfo: {
        // Region is only part of the cache key for queries that need it (e.g. the
        // pipeline-config tool). Queries that omit region fall back to the unique id.
        keyFields: (object) => (object.region != null ? ['teamId', 'region', 'id'] : ['id']),
      },
      PipelineInputConfigInfo: {
        keyFields: ['venueId', 'id', 'region'],
      },
      PipelineMonitor: {
        keyFields: ['pipelineConfig', ['id']],
      },
      Query: {
        fields: {
          pipelineMonitors: {
            merge(_existing = [], incoming: unknown[]) {
              return incoming;
            },
          },
        },
      },
    },
  });

  return new ApolloClient({
    cache,
    defaultOptions: {
      mutate: { errorPolicy: 'all' },
      query: { errorPolicy: 'all' },
      watchQuery: {
        errorPolicy: 'all',
        /*
         * Prevents polling if the user's browser tab is not visible.
         * https://github.com/apollographql/apollo-client/pull/11397
         */
        skipPollAttempt: () => document.hidden,
      },
    },
    devtools: { enabled: true },
    link,
  });
}

export interface ApolloProps {
  children: ReactNode;
  mlbOperatorGatewayProxyConfig: MlbGatewayProxy;
}

export function Apollo({ children, mlbOperatorGatewayProxyConfig }: ApolloProps) {
  const { baseUrl, endpoint, endpoints, wssBase } = mlbOperatorGatewayProxyConfig;
  const uri = `${baseUrl}${endpoints[endpoint]}`;
  const wsUrl = `${wssBase}${endpoints[endpoint]}`;

  const client = useMemo(() => createApolloClientConfig(uri, wsUrl), [uri, wsUrl]);

  return <ApolloProvider client={client}>{children}</ApolloProvider>;
}
